import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContextType, User, AccountResponse } from '../types/account';
import { ApiResponse } from '../types/api';
import api from '../services/api';

const AuthContext = createContext<AuthContextType | null>(null);

// Add a broadcast channel for cross-tab synchronization
const authChannel = new BroadcastChannel('auth_channel');

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const updateUserFromResponse = (response: AccountResponse) => {
    if (response.success && response.user) {
      // Extract data from _doc or fall back to direct properties
      const userData = response.user;
      const updatedUser = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        state: userData.state,
        stripe: userData.stripe,
        configs: userData.configs,
      };
      setUser(updatedUser);
    }
  };

  // Add a debug effect to track user changes
  useEffect(() => {
    console.log('👤 Current user state:', user);
  }, [user]);

  // Create a wrapped setUser function to broadcast changes
  const updateUser = useCallback((newUser: User | null) => {
    setUser(newUser);
    // Broadcast the user update to other tabs
    authChannel.postMessage({ type: 'USER_UPDATE', user: newUser });
  }, []);

  // Listen for broadcast messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'USER_UPDATE') {
        setUser(event.data.user);
      }
    };

    authChannel.addEventListener('message', handleMessage);
    return () => authChannel.removeEventListener('message', handleMessage);
  }, []);

  // Update the checkAuth function
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) return;

      try {
        const response = await api.post<{ loggedIn: boolean; user: any }>('/auth/loggedin');
        if (response.success && response.data?.loggedIn && response.data.user) {
          const userData = response.data.user;
          updateUser({
            id: userData.id || userData._id,
            email: userData.email,
            name: userData.name,
            state: userData.state,
            stripe: userData.stripe,
            configs: userData.configs
          });
        } else {
          localStorage.removeItem('authToken');
          setToken(null);
          updateUser(null);
        }
      } catch (error) {
        localStorage.removeItem('authToken');
        setToken(null);
        updateUser(null);
      }
    };

    if (token && !user) {
      checkAuth();
    }
  }, [token, updateUser]);

  // Separate effect for OAuth callback handling
  useEffect(() => {
    if (location.pathname === '/auth/dashboard') {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const userStr = params.get('user');
      const success = params.get('success');

      if (success === 'true' && token && userStr) {
        try {
          const decodedToken = decodeURIComponent(token);
          const decodedUser = JSON.parse(decodeURIComponent(userStr));

          // Set them in the popup's local storage immediately
          localStorage.setItem('authToken', decodedToken);

          // Broadcast to any listening tabs
          authChannel.postMessage({
            type: 'oauth_success',
            token: decodedToken,
            user: decodedUser
          });

          // Post message to opener if it survived COOP headers
          if (window.opener) {
            window.opener.postMessage({
              type: 'oauth_success',
              token: decodedToken,
              user: decodedUser
            }, window.location.origin);
          }

          // Try close
          window.close();

          // Fallback if browser blocks script closure
          setTimeout(() => {
            if (!window.closed) {
              setToken(decodedToken);
              updateUser(decodedUser);
              navigate('/dashboard');
            }
          }, 500);

        } catch (error) {
          console.error('Error processing OAuth data:', error);
        }
      }
    }
  }, [location, navigate, updateUser]);

  // Message listener for OAuth (from window.opener or authChannel)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Allow identical origin messages OR BroadcastChannel generic messages
      if (event.origin && event.origin !== window.location.origin) return;

      if (event.data.type === 'oauth_success') {
        localStorage.setItem('authToken', event.data.token);
        setToken(event.data.token);
        updateUser(event.data.user);
        navigate('/auth/dashboard');
      }
    };

    window.addEventListener('message', handleMessage);
    authChannel.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      authChannel.removeEventListener('message', handleMessage);
    };
  }, [navigate, updateUser]);

  const loginWithGoogle = () => {
    const width = 600;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    let apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.port === '3000') {
        apiBase = `${window.location.protocol}//${window.location.hostname}:4000`;
      } else {
        apiBase = window.location.origin;
      }
    }

    // Open OAuth in popup
    const popup = window.open(
      `${apiBase}/auth/google`,
      'Login with Google',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );

    // Focus the popup
    if (popup) popup.focus();
  };

  const register = async (email: string, name: string): Promise<ApiResponse> => {
    console.log('🔵 Starting registration in AuthContext');
    try {
      console.log('🟡 About to make API call with:', { email, name });

      const response = await api.post<AccountResponse>('/auth/register', { email, name })
        .catch(error => {
          console.log('🔴 API call rejected:', error);
          throw error;
        });

      console.log('🟢 Raw API Response received:', response);

      if (!response) {
        console.log('⚫ No response received');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.success && response.data) {
        console.log('✅ Registration successful:', response.data);
        return {
          success: true,
          data: response.data
        };
      }

      console.log('❌ Registration failed:', response.error);
      return {
        success: false,
        error: response.error || 'Registration failed'
      };

    } catch (error) {
      console.error('💥 Caught unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred during registration'
      };
    }
  };

  const requestPin = async (email: string): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auth/pin', { email });
      return response;
    } catch (error) {
      console.error('PIN request error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to request PIN'
      };
    }
  };

  const verifyPin = async (email: string, pin: string) => {
    try {
      const response = await api.post<{ success: boolean; token: string; user: User }>('/auth/verify', { email, pin });

      if (response.success && response.data?.success) {
        localStorage.setItem('authToken', response.data.token);
        setToken(response.data.token);
        updateUser(response.data.user);
        navigate('/auth/dashboard');
        return { success: true };
      }
      return response.data;
    } catch (error) {
      console.error('PIN verification error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const response = await api.post('/auth/logout');
      if (response.success) {
        console.log('Logout successful');
      } else {
        console.warn('Logout response indicated failure:', response.data);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.clear();
      setToken(null);
      updateUser(null);
      window.location.href = '/auth/login';
    }
  };

  const checkLoginStatus = async (): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await api.post<{ loggedIn: boolean; user: User }>('/auth/loggedin');

      if (response.success && response.data?.loggedIn && response.data.user) {
        updateUser(response.data.user);
        return true;
      } else {
        localStorage.removeItem('authToken');
        setToken(null);
        updateUser(null);
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('authToken');
      setToken(null);
      updateUser(null);
      return false;
    }
  };

  // Remove checkLoginStatus from the returned value since we handle it internally
  const value = {
    token,
    user,
    setToken,
    setUser,
    logout,
    loginWithGoogle,
    register,
    requestPin,
    verifyPin,
    checkLoginStatus,
    updateUserFromResponse
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
